import { useCallback, useEffect, useMemo, useState } from 'react';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { useGetPaymentReportsQuery, useLazyGetPaymentReceiptQuery } from '../../../../api/endpoints/creditCardPayment';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { toastService } from '../../../../utils/toastService';
import { formatDateForApi } from '../../../../utils/dateUtils';

/**
 * Ayın ilk gününü döndürür
 */
const getFirstDayOfMonth = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
};

/**
 * Ayın son gününü döndürür
 */
const getLastDayOfMonth = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

const usePaymentReportsData = () => {
    const { th } = useLanguage();

    // Tablo verileri
    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);

    // Pagination state
    const [pagination, setPagination] = useState({ PageNumber: 1, PageSize: 10 });

    // Sorting state
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState(-1); // -1: DESC, 1: ASC

    // Filter state - varsayılan ay başı-sonu
    const [filters, setFilters] = useState({
        startDate: getFirstDayOfMonth(),
        endDate: getLastDayOfMonth(),
        searchText: ''
    });

    // Makbuz indirme için
    const [getPaymentReceipt, { isLoading: isLoadingReceipt }] = useLazyGetPaymentReceiptQuery();

    // API query params
    const queryParams = useMemo(() => ({
        pageNumber: pagination.PageNumber,
        pageSize: pagination.PageSize,
        startDate: formatDateForApi(filters.startDate),
        endDate: formatDateForApi(filters.endDate),
        searchText: filters.searchText || '',
        sortField: sortField,
        sortOrder: sortOrder === 1 ? 'asc' : 'desc'
    }), [pagination, filters, sortField, sortOrder]);

    // API çağrısı
    const { data: listData, isLoading, isFetching, refetch } = useGetPaymentReportsQuery(queryParams, {
        refetchOnMountOrArgChange: true
    });

    // Veri güncelleme
    useEffect(() => {
        if (listData) {
            setRows(Array.isArray(listData.data) ? listData.data : []);
            setTotalCount(listData.totalCount || 0);
        }
    }, [listData]);

    const loading = isLoading || isFetching;

    // Sayfa değişimi
    const onPageChange = useCallback((event) => {
        const newPageNumber = Math.floor(event.first / event.rows) + 1;
        setPagination((prev) => ({
            ...prev,
            PageNumber: newPageNumber,
            PageSize: event.rows
        }));
    }, []);

    // Sıralama değişimi
    const onSort = useCallback((event) => {
        setSortField(event.sortField);
        setSortOrder(event.sortOrder);
        setPagination((prev) => ({ ...prev, PageNumber: 1 }));
    }, []);

    // Filtreleri sıfırla
    const handleResetFilters = useCallback(() => {
        setFilters({
            startDate: getFirstDayOfMonth(),
            endDate: getLastDayOfMonth(),
            searchText: ''
        });
        setPagination((prev) => ({ ...prev, PageNumber: 1 }));
    }, []);

    // Filtreleri uygula (sadece "Uygula" butonuna basıldığında çağrılır)
    const handleApplyFilters = useCallback((newFilters) => {
        setFilters({
            startDate: newFilters.startDate,
            endDate: newFilters.endDate,
            searchText: newFilters.searchText || ''
        });
        setPagination((prev) => ({ ...prev, PageNumber: 1 }));
    }, []);

    // Makbuz indirme
    const downloadReceipt = useCallback(async (transactionId) => {
        try {
            const result = await getPaymentReceipt(transactionId).unwrap();
            if (result.success && result.data) {
                return result.data;
            } else {
                toastService.error(th('paymentReports.errors.receiptNotFound') || 'Makbuz bulunamadı');
                return null;
            }
        } catch (error) {
            console.error('Error fetching receipt:', error);
            toastService.error(th('paymentReports.errors.receiptFetchError') || 'Makbuz alınırken hata oluştu');
            return null;
        }
    }, [getPaymentReceipt, th]);

    // Filter fields tanımı
    const filterFields = useMemo(() => [
        {
            field: 'startDate',
            headerName: th('paymentReports.filters.startDate') || 'Başlangıç Tarihi',
            type: 'date'
        },
        {
            field: 'endDate',
            headerName: th('paymentReports.filters.endDate') || 'Bitiş Tarihi',
            type: 'date'
        },
        {
            field: 'searchText',
            headerName: th('paymentReports.filters.search') || 'Ara (Bayi Kodu, Bayi Adı...)',
            type: 'text'
        }
    ], [th]);

    return {
        // Tablo verileri
        rows,
        loading,
        totalCount,
        pagination,
        
        // Sıralama
        sortField,
        sortOrder,
        onSort,
        
        // Sayfalama
        onPageChange,
        
        // Filtreler
        filters,
        filterFields,
        handleResetFilters,
        handleApplyFilters,
        
        // Makbuz
        downloadReceipt,
        isLoadingReceipt,
        
        // Yenile
        refetch
    };
};

export default usePaymentReportsData;
